/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
/**
 * @fileoverview FileDisplay shows a PDF/file row with icon, name, size (or loading/error state), and download action.
 * Used inside ProposalDisplayModal for quotation and brochure files.
 */

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ProposalDisplayModal.module.css";
import { ReactComponent as PDFIcon } from "../../assets/icons/ProposalDisplayModal/PDFIcon.svg";
import { ReactComponent as DownloadIcon } from "../../assets/icons/ProposalDisplayModal/DownloadIcon.svg";
import { resolveAccessibleAssetUrl } from "../../utils/common";

/** Decimal places kept when a byte count is shown as a unit size. */
const DECIMAL_PLACES = 2;

/** Content types that are error pages or API payloads, not a brochure or quote the user can save. */
const NON_FILE_BODY_PATTERN = /text\/|json|html|xml/i;

/**
 * True when a successful response is still not a downloadable file.
 * S3 and the asset proxy can return XML or JSON with HTTP 200.
 * @param {Response} response
 * @param {Blob} blob
 * @returns {boolean}
 */
export function isNonFileBody(response, blob) {
  const type = `${blob?.type || ""} ${response.headers.get("content-type") || ""}`;
  return NON_FILE_BODY_PATTERN.test(type);
}

/**
 * Loads the object only to read its size. A failed status or a non-file body is unavailable.
 * @param {string} resolvedUrl
 * @returns {Promise<{ ok: true, size: number } | { ok: false }>}
 */
async function readFileSizeProbe(resolvedUrl) {
  if (!resolvedUrl) {
    return { ok: false };
  }
  const response = await fetch(resolvedUrl);
  if (!response.ok) {
    return { ok: false };
  }
  const blob = await response.blob();
  if (isNonFileBody(response, blob)) {
    return { ok: false };
  }
  return { ok: true, size: blob.size };
}

/**
 * Displays a file row only after the stored object is found.
 * A missing reference or a failed probe renders nothing, so a deleted bucket object stays hidden.
 *
 * @param {Object} props
 * @param {string} [props.fileUrl] - URL of the file (used to fetch size and for download).
 * @param {string} [props.fileName] - Display name for the file.
 * @param {function(): void} props.onDownload - Called when the user clicks the download icon.
 * @param {function(boolean): void} [props.onAvailabilityChange] - Reports whether the object was found.
 * @returns {JSX.Element|null}
 */
const FileDisplay = ({ fileUrl, fileName, onDownload, onAvailabilityChange }) => {
  const { t } = useTranslation();
  const [fileSize, setFileSize] = useState(null);
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let cancelled = false;

    const markMissing = () => {
      if (!cancelled) {
        setFileSize(null);
        setStatus("missing");
      }
    };

    if (!fileUrl) {
      markMissing();
      return () => {
        cancelled = true;
      };
    }

    setStatus("checking");
    setFileSize(null);

    const fetchFileSize = async () => {
      try {
        const resolvedUrl = await resolveAccessibleAssetUrl(fileUrl);
        const probe = await readFileSizeProbe(resolvedUrl);
        if (cancelled) {
          return;
        }
        if (!probe.ok) {
          markMissing();
          return;
        }
        setFileSize(probe.size);
        setStatus("ready");
      } catch (err) {
        logger.error("Error fetching file size:", { err: sanitizeError(err) });
        markMissing();
      }
    };

    fetchFileSize();
    return () => {
      cancelled = true;
    };
  }, [fileUrl]);

  useEffect(() => {
    onAvailabilityChange?.(status === "ready");
  }, [onAvailabilityChange, status]);

  /**
   * Formats a byte count as a human-readable size (e.g. "1.5 MB"). Zero bytes returns the "empty file" translation.
   * @param {number} bytes - File size in bytes.
   * @returns {string} Formatted size string or translated "empty file" when bytes is 0.
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) {
      return t("components.proposalDisplayModal.fileSizeEmpty");
    }
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formatted = parseFloat(
      (bytes / Math.pow(k, i)).toFixed(DECIMAL_PLACES),
    );
    return `${formatted} ${sizes[i]}`;
  };

  if (status !== "ready" || fileSize == null) {
    return null;
  }

  return (
    <div className={styles.pastelBlueBlackground}>
      <div className={styles.pdfFileInfo}>
        <PDFIcon className={styles.pdfIcon} />
        <div className={styles.fileDetails}>
          <span className={styles.fileName}>{fileName}</span>
          <span className={styles.fileSize}>{formatFileSize(fileSize)}</span>
        </div>
      </div>
      <DownloadIcon
        className={styles.downloadIcon}
        data-testid="file-download"
        onClick={onDownload}
      />
    </div>
  );
};

export default FileDisplay;
